import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Sparkles, TrendingUp, Lightbulb, Heart, Scale, 
  ArrowLeft, Download, Share2, Copy, Check, ExternalLink,
  Twitter, Linkedin, Mail, MessageCircle, Loader2, AlertCircle
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublicEvaluation = () => {
  const { evaluationId } = useParams();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchEvaluation = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/evaluations/${evaluationId}`);
        setEvaluation(response.data);
      } catch (err) {
        console.error('Error fetching evaluation:', err);
        setError(err.response?.status === 404 ? 'Evaluation not found' : 'Failed to load evaluation');
      } finally {
        setLoading(false);
      }
    };

    if (evaluationId) {
      fetchEvaluation();
    }
  }, [evaluationId]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast({ title: "Link Copied!", description: "Share it with your peers" });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({ title: "Copy Failed", variant: "destructive" });
    }
  };

  const handleDownloadPDF = () => {
    const pdfUrl = `${BACKEND_URL}/api/evaluations/${evaluationId}/pdf`;
    window.open(pdfUrl, '_blank');
    toast({ title: "PDF Opening!", description: "Your certificate will open in a new tab" });
  };

  const shareToTwitter = () => {
    const text = `🎯 Check out this research evaluation on IdeaFountain!\n\n"${evaluation?.paper_title?.substring(0, 50)}..."\nOriginality Score: ${evaluation?.oi_total?.toFixed(1)}/10\n\n#Research #Innovation`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`;
    window.open(url, '_blank');
  };

  const shareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`;
    window.open(url, '_blank');
  };

  const shareToWhatsApp = () => {
    const text = `🎯 Check out this research evaluation!\n\n"${evaluation?.paper_title}"\nOriginality Score: ${evaluation?.oi_total?.toFixed(1)}/10\n\n${window.location.href}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const shareByEmail = () => {
    const subject = `Research Evaluation - ${evaluation?.paper_title}`;
    const body = `Hi,\n\nCheck out this research evaluation!\n\nPaper: ${evaluation?.paper_title}\nOriginality Score: ${evaluation?.oi_total?.toFixed(1)}/10\nAssessment: ${evaluation?.msa_assessment}\n\nView full evaluation: ${window.location.href}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-emerald-600';
    if (score >= 6) return 'text-blue-600';
    if (score >= 4) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 8) return 'bg-emerald-100 border-emerald-200';
    if (score >= 6) return 'bg-blue-100 border-blue-200';
    if (score >= 4) return 'bg-amber-100 border-amber-200';
    return 'bg-red-100 border-red-200';
  };

  const getInterpretation = (score) => {
    if (score >= 9) return 'Exceptional';
    if (score >= 8) return 'Excellent';
    if (score >= 7) return 'Strong';
    if (score >= 6) return 'Good';
    if (score >= 5) return 'Moderate';
    return 'Developing';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading evaluation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">{error}</h2>
            <p className="text-slate-600 mb-6">
              This evaluation may have been removed or the link is invalid.
            </p>
            <Button onClick={() => navigate('/')} className="bg-purple-600 hover:bg-purple-700">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleCopyLink}
            >
              {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
            <Button 
              size="sm"
              onClick={handleDownloadPDF}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Download className="w-4 h-4 mr-1" />
              PDF
            </Button>
          </div>
        </div>

        {/* Main Card */}
        <Card className="shadow-xl border-0 overflow-hidden">
          {/* Gradient Header */}
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 p-6 text-white">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <Badge className="bg-white/20 text-white mb-3">
                  {evaluation?.paper_type || 'Research Paper'}
                </Badge>
                <h1 className="text-2xl md:text-3xl font-bold mb-2">
                  {evaluation?.paper_title || 'Untitled Evaluation'}
                </h1>
                <p className="text-white/80 text-sm">
                  Evaluated on {new Date(evaluation?.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              
              {/* OI Score Badge */}
              <div className="text-center bg-white/20 backdrop-blur-sm rounded-xl p-4 ml-4">
                <div className="text-4xl font-bold">
                  {evaluation?.oi_total?.toFixed(1)}
                </div>
                <div className="text-xs uppercase tracking-wider opacity-80">OI Score</div>
              </div>
            </div>
          </div>

          <CardContent className="p-6">
            {/* OI Breakdown */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Originality Index Breakdown
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Novelty', value: evaluation?.oi_novelty, icon: Lightbulb, color: 'purple' },
                  { label: 'Resonance', value: evaluation?.oi_resonance, icon: Heart, color: 'pink' },
                  { label: 'Synthesis', value: evaluation?.oi_synthesis, icon: TrendingUp, color: 'blue' },
                  { label: 'Ethical', value: evaluation?.oi_ethical, icon: Scale, color: 'emerald' }
                ].map((item) => (
                  <div 
                    key={item.label}
                    className={`p-4 rounded-xl border ${getScoreBg(item.value)} text-center`}
                  >
                    <item.icon className={`w-6 h-6 mx-auto mb-2 ${getScoreColor(item.value)}`} />
                    <div className={`text-2xl font-bold ${getScoreColor(item.value)}`}>
                      {item.value?.toFixed(1)}
                    </div>
                    <div className="text-xs text-slate-600 uppercase tracking-wide">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* MSA Assessment */}
            {evaluation?.msa_assessment && (
              <div className="mb-8 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">MSA Assessment</h3>
                <p className="text-slate-700">{evaluation.msa_assessment}</p>
                
                <div className="flex gap-4 mt-4">
                  <div className="flex-1 text-center p-3 bg-white rounded-lg">
                    <div className="text-xl font-bold text-blue-600">
                      {evaluation?.msa_mirror_pct}%
                    </div>
                    <div className="text-xs text-slate-500">Mirror</div>
                  </div>
                  <div className="flex-1 text-center p-3 bg-white rounded-lg">
                    <div className="text-xl font-bold text-orange-600">
                      {evaluation?.msa_spark_pct}%
                    </div>
                    <div className="text-xs text-slate-500">Spark</div>
                  </div>
                </div>
              </div>
            )}

            {/* Interpretation */}
            <div className="mb-8 p-4 bg-slate-50 rounded-xl">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Overall Interpretation</h3>
              <div className="flex items-center gap-3">
                <Badge className={`${getScoreBg(evaluation?.oi_total)} ${getScoreColor(evaluation?.oi_total)} border px-3 py-1`}>
                  {getInterpretation(evaluation?.oi_total)}
                </Badge>
                <span className="text-slate-600">
                  {evaluation?.oi_interpretation || `This paper demonstrates ${getInterpretation(evaluation?.oi_total).toLowerCase()} originality.`}
                </span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="border-t pt-6">
              <h3 className="text-sm font-medium text-slate-500 mb-3">Share this evaluation</h3>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={shareToTwitter}>
                  <Twitter className="w-4 h-4 mr-1" />
                  Twitter
                </Button>
                <Button variant="outline" size="sm" onClick={shareToLinkedIn}>
                  <Linkedin className="w-4 h-4 mr-1" />
                  LinkedIn
                </Button>
                <Button variant="outline" size="sm" onClick={shareToWhatsApp}>
                  <MessageCircle className="w-4 h-4 mr-1" />
                  WhatsApp
                </Button>
                <Button variant="outline" size="sm" onClick={shareByEmail}>
                  <Mail className="w-4 h-4 mr-1" />
                  Email
                </Button>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8 p-6 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl text-white text-center">
              <h3 className="text-xl font-bold mb-2">Get Your Own Evaluation</h3>
              <p className="text-white/80 mb-4">
                Analyze your research paper with AI-powered originality scoring
              </p>
              <Button 
                onClick={() => navigate('/quick-evaluate')}
                className="bg-white text-purple-600 hover:bg-white/90"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Try IdeaFountain Free
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Certificate ID */}
        <div className="text-center mt-6 text-sm text-slate-500">
          Certificate ID: <span className="font-mono">{evaluation?.evaluation_id}</span>
        </div>
      </div>
    </div>
  );
};

export default PublicEvaluation;
